import { Head, router } from "@inertiajs/react"
import { Plus } from "lucide-react"
import { useState } from "react"

import { BidsKanban, type BidCard, type BidStatus } from "@/components/bids-kanban"
import { Button } from "@/components/ui/button"
import { DataTable, Primary, Td, Th, Thead, Tr } from "@/components/steel/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Field, PageHeader, Section, StatusBadge, ViewToggle } from "@/components/steel"
import { Input } from "@/components/ui/input"
import { formatCurrency, shortMoney } from "@/lib/utils"

type BidRow = BidCard & { updatedAt: string }

const NEXT_STEP: Record<BidStatus, string> = {
  draft: "Mark sent",
  sent: "Accept or reject",
  accepted: "Convert to project",
  rejected: "Revise",
  converted: "View project",
}

function nextAction(bid: BidRow) {
  switch (bid.status) {
    case "draft":
      return () => router.patch(`/bids/${bid.id}`, { status: "sent" }, { preserveScroll: true })
    case "rejected":
      return () => router.visit(`/bids/${bid.id}/edit`)
    case "converted":
      return bid.convertedProjectId
        ? () => router.visit(`/projects/${bid.convertedProjectId}`)
        : undefined
    default:
      return undefined
  }
}

export default function BidsIndex({ bids }: { bids: BidRow[] }) {
  const [view, setView] = useState<"table" | "board">("table")
  const [convertBid, setConvertBid] = useState<BidRow | null>(null)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  const open = bids.filter((b) => b.status === "draft" || b.status === "sent")
  const pipeline = open.reduce((sum, b) => sum + (b.calculated.price ?? 0), 0)

  const openConvert = (bid: BidRow) => {
    const today = new Date()
    const later = new Date()
    later.setMonth(later.getMonth() + 3)
    setStartDate(today.toISOString().slice(0, 10))
    setEndDate(later.toISOString().slice(0, 10))
    setConvertBid(bid)
  }

  return (
    <>
      <Head title="Bids" />

      <PageHeader
        title="Bids"
        meta={`${open.length} open · ${shortMoney(pipeline)} in the pipeline`}
        actions={
          <>
            <ViewToggle
              value={view}
              onChange={setView}
              options={[
                { value: "table", label: "Table" },
                { value: "board", label: "Board" },
              ]}
            />
            <Button size="sm" onClick={() => router.visit("/bids/new")}>
              <Plus />
              New bid
            </Button>
          </>
        }
      />

      {view === "table" ? (
        <Section className="pb-7 pt-3.5">
          <DataTable>
            <Thead>
              <Th>Bid</Th>
              <Th>Client</Th>
              <Th align="right">Sq ft</Th>
              <Th align="right">$/sq ft</Th>
              <Th align="right">Estimate</Th>
              <Th>Status</Th>
              <Th align="right">Next step</Th>
            </Thead>
            <tbody>
              {bids.map((bid) => {
                const sqft = bid.calculated.totalSqFt ?? 0
                const price = bid.calculated.price ?? 0
                const action = bid.status === "accepted" ? () => openConvert(bid) : nextAction(bid)

                return (
                  <Tr key={bid.id} onClick={() => router.visit(`/bids/${bid.id}/edit`)}>
                    <Td>
                      <Primary
                        title={bid.name || "Untitled bid"}
                        sub={`${bid.id} · Rev ${bid.revision} · ${new Date(bid.updatedAt).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                      />
                    </Td>
                    <Td muted>{bid.contact.companyName || bid.contact.clientName || "—"}</Td>
                    <Td align="right" num muted>
                      {sqft.toLocaleString()}
                    </Td>
                    <Td align="right" num muted>
                      {sqft > 0 ? `$${(price / sqft).toFixed(2)}` : "—"}
                    </Td>
                    <Td align="right" num accent>
                      {formatCurrency(price)}
                    </Td>
                    <Td>
                      <StatusBadge status={bid.status} />
                    </Td>
                    <Td align="right">
                      <Button
                        variant="outline"
                        size="xs"
                        disabled={!action}
                        onClick={(e) => {
                          e.stopPropagation()
                          action?.()
                        }}
                      >
                        {NEXT_STEP[bid.status]}
                      </Button>
                    </Td>
                  </Tr>
                )
              })}
            </tbody>
          </DataTable>
        </Section>
      ) : (
        <BidsKanban bids={bids} />
      )}

      <Dialog open={convertBid !== null} onOpenChange={(next) => !next && setConvertBid(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to a project</DialogTitle>
            <DialogDescription>
              {convertBid?.name || "This bid"} becomes a project with its milestones carried over.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Start">
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="Finish">
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setConvertBid(null)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                if (!convertBid) return
                router.post(
                  `/bids/${convertBid.id}/convert`,
                  { start_date: startDate, end_date: endDate },
                  { onSuccess: () => setConvertBid(null) },
                )
              }}
            >
              Convert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
